import { graphql } from '@mp/graphql/generated';
import { useGraphQLQuery } from '@mp/hooks/useGraphQLRequest';
import { type ResultOf } from '@graphql-typed-document-node/core';
import { useCurrentUser } from '@mp/hooks';

export const EHRsGql = graphql(`
  query GetEHRsForEHRForm($input: EhrSystemQueryInput) {
    ehrSystems(input: $input) {
      id
      label
    }
  }
`);

const AssignmentRequestsGql = graphql(`
  query GetAssignmentRequestsForEHRForm($input: AssignmentRequestQueryInput) {
    assignmentRequests(input: $input) {
      id
    }
  }
`);

type EHRsProps = ResultOf<typeof EHRsGql>;

type AssignmentRequests = ResultOf<typeof AssignmentRequestsGql>;

export const useEHRs = () => {
  const { data, isLoading } = useGraphQLQuery<EHRsProps>({
    operationName: 'GetEHRsForEHRForm',
    query: EHRsGql,
    variables: { input: { filter: { active: true }, orderBy: { name: true } } },
    queryKey: ['ehrs'],
  });

  return {
    ehrs: data?.ehrSystems || [],
    isLoading,
  };
};

export const useEHRExperiences = () => {
  const { currentUser } = useCurrentUser();
  const professional_id = currentUser?.professional?.id;

  const { data, isLoading } = useGraphQLQuery<EHRsProps>({
    operationName: 'GetEHRsForEHRForm',
    query: EHRsGql,
    variables: {
      input: { search: { professional_id }, filter: { active: true }, orderBy: { name: true } },
    },
    queryKey: ['ehrs', professional_id],
  });

  return {
    userEhrs: data?.ehrSystems || [],
    isLoading,
  };
};

export const useAssignmentRequestsForEHRs = (options: { assignmentId?: number } = {}) => {
  const { currentUser } = useCurrentUser();
  const professional_id = currentUser?.professional?.id;

  const assignment_id = options.assignmentId;
  const enabled = assignment_id === undefined ? true : !!assignment_id;

  const { data: assignmentRequestsData, isLoading } = useGraphQLQuery<AssignmentRequests>({
    operationName: 'GetAssignmentRequestsForEHRForm',
    query: AssignmentRequestsGql,
    variables: {
      input: { filter: { assignment_id }, orderBy: { updated_at: false } },
      professional_id,
    },
    enabled,
  });
  const assignmentRequests = assignmentRequestsData?.assignmentRequests || [];

  return { assignmentRequests, isLoading };
};
